import type { ModuleMountOptions } from '@opengov/psp-contract';
import React, { type ReactNode, useEffect, useState } from 'react';
import { ldFlags } from '../common/flags';
import { useFeatureFlag } from '../providers/FeatureFlagsProvider';
import { noopOgAnalytics, OgAnalyticsContext, type ReportingOgAnalytics } from './ogAnalyticsClient';
import { loadOgAnalyticsRuntime } from './ogAnalyticsLoader';
import type { OgAnalyticsRuntime } from './ogAnalyticsRuntime';

type OgAnalyticsGateProps = Readonly<{
  mountOptions?: ModuleMountOptions;
  children: ReactNode;
}>;

/**
 * Supplies `useOgAnalytics()` to the app. The client is a no-op unless this is an analytics build
 * (integration or local development — see `ogAnalyticsLoader.ts`) and
 * `product-reporting-feature-enable-posthog` is on, in which case the package is fetched on demand
 * and its client swapped in.
 *
 * Must sit inside `FeatureFlagsProvider` to read the flag.
 */
export function OgAnalyticsGate({ mountOptions, children }: OgAnalyticsGateProps) {
  const isFlagOn = useFeatureFlag(ldFlags.productReportingFeatureEnablePosthog);
  const isEnabled = !!loadOgAnalyticsRuntime && isFlagOn;
  const [runtimeComponent, setRuntimeComponent] = useState<typeof OgAnalyticsRuntime | null>(null);
  const [client, setClient] = useState<ReportingOgAnalytics>(noopOgAnalytics);

  useEffect(() => {
    if (!loadOgAnalyticsRuntime || !isEnabled || runtimeComponent) {
      return undefined;
    }
    let cancelled = false;
    loadOgAnalyticsRuntime()
      .then((runtimeModule) => {
        if (!cancelled) {
          setRuntimeComponent(() => runtimeModule.OgAnalyticsRuntime);
        }
      })
      .catch((error: unknown) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load @opengov/analytics:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [isEnabled, runtimeComponent]);

  const Runtime = runtimeComponent;

  return (
    <OgAnalyticsContext.Provider value={isEnabled ? client : noopOgAnalytics}>
      {isEnabled && Runtime ? <Runtime mountOptions={mountOptions} onClient={setClient} /> : null}
      {children}
    </OgAnalyticsContext.Provider>
  );
}
